import api from '@/app/api';
import {create} from 'zustand';
import { SearchQuery } from './interface/searchFlightInterface';

// Cache for storing recent search results
const searchCache = new Map();
const CACHE_EXPIRY_TIME = 5 * 60 * 1000; // 5 minutes
const HOTEL_SEARCH_QUERY_KEY = 'hotelSearchQuery';
const HOTEL_REVIEW_CONTEXT_KEY = 'hotelReviewContext';

export function saveHotelSearchQuery(searchQuery: any) {
  if (typeof window === 'undefined') return;
  if (searchQuery?.checkinDate && searchQuery?.checkoutDate) {
    localStorage.setItem(HOTEL_SEARCH_QUERY_KEY, JSON.stringify(searchQuery));
  }
}

export function loadHotelSearchQuery() {
  if (typeof window === 'undefined') return null;
  try {
    const stored = localStorage.getItem(HOTEL_SEARCH_QUERY_KEY);
    return stored ? JSON.parse(stored) : null;
  } catch {
    return null;
  }
}

export function saveHotelReviewContext(context: {
  hotelId?: string;
  optionId?: string;
  correlationId?: string;
  reviewHash?: string;
  searchQuery?: any;
  detailsIpr?: boolean;
}) {
  if (typeof window === 'undefined') return;
  if (!context?.hotelId) return;
  localStorage.setItem(HOTEL_REVIEW_CONTEXT_KEY, JSON.stringify(context));
}

export function loadHotelReviewContext() {
  if (typeof window === 'undefined') return null;
  try {
    const stored = localStorage.getItem(HOTEL_REVIEW_CONTEXT_KEY);
    return stored ? JSON.parse(stored) : null;
  } catch {
    return null;
  }
}

function hotelAvailabilityMessage(error: any) {
  const data = error?.response?.data;
  const nestedMessage = data?.message;
  return (
    data?.availability?.message ||
    data?.errors?.[0]?.message ||
    (Array.isArray(nestedMessage) ? nestedMessage[0]?.message : null) ||
    (nestedMessage?.errors?.[0]?.message) ||
    (typeof nestedMessage === 'string' &&
    nestedMessage !== 'Bad Request Exception'
      ? nestedMessage
      : null) ||
    'No rooms available for the selected dates. The hotel may be sold out.'
  );
}

const useHotelBookingStore = create((set, get) => ({
  hotelSearchSuggestions: [], 
  hotelSearchResult: [],
  session:{},
  getDetail:{},
  isLoading:false,
  isHotelSearchSuggestionsLoading:false,
  isHotelSearchLoading:false,
  isSessionLoading:false,
  error: null,
  receiveError:false,
  bookingRequest:{},
  requestUrl:'/',
  isErrors:false,
  nationalities: [],
  isNationalitiesLoading: false,
  cancellationCharges: {},
  isCancellationChargesLoading: false,

  searchHotelSuggestion: async (value:string) => {
    // Don't search if value is too short
    if (!value || value.length < 2) {
      set({ isHotelSearchSuggestionsLoading: false, hotelSearchSuggestions: [] });
      return [];
    }

    // Check cache first
    const cacheKey = `hotel_suggestion_${value.toLowerCase()}`;
    const cachedResult = searchCache.get(cacheKey);
    if (cachedResult) {
      set({ isHotelSearchSuggestionsLoading: false, hotelSearchSuggestions: cachedResult });
      return cachedResult;
    }

    set({ isHotelSearchSuggestionsLoading: true }); // Set loading state
    try {
      const response = await api.get(`/search/hotelSuggestions?value=${encodeURIComponent(value)}`);
      if (!response?.data) {
        throw new Error('Network response was not ok');
      }
      const data = response.data;
      
      // Cache the result
      searchCache.set(cacheKey, data);
      setTimeout(() => searchCache.delete(cacheKey), CACHE_EXPIRY_TIME);
      
      set({ isHotelSearchSuggestionsLoading: false, hotelSearchSuggestions: data });
      return data;
    } catch (error:any) {
      set({isHotelSearchSuggestionsLoading: false, error: error});
      return [];
    }
  },
  setHotelsSearch: async (data:SearchQuery)=>{
    set({ isHotelSearchLoading: true, error: null }); // Set loading state
    try {
      set({ hotelSearchResult:data , isHotelSearchLoading: false, error: null });
    } catch (error:any) {
      set({ error: error, isHotelSearchLoading: false }); // Handle errors
    }
  },

  getHotelDetail: async (payload:any)=>{
    set({ isLoading: true, error: null }); // Set loading state
    try {
      const hotelId = typeof payload === 'object' ? payload?.id : payload;
      const searchQuery = payload?.searchQuery || loadHotelSearchQuery();
      if (searchQuery) {
        saveHotelSearchQuery(searchQuery);
      }
      const response :any= await api.post(`/search/hotel/details` , {
        id: hotelId,
        searchQuery,
        correlationId: payload?.correlationId,
      });
      saveHotelReviewContext({
        hotelId,
        correlationId: response.data?.correlationId,
        reviewHash: response.data?.reviewHash,
        searchQuery,
      });
      set({ getDetail:response.data,isLoading: false, error: null });
      return response
    } catch (error:any) {
      set({ error: error, isLoading: false });
    }
  },
  updateHotelDetailSearch: async (data:any)=>{
    set({ isLoading: true, error: null }); // Set loading state
    const searchQuery = data?.searchQuery || loadHotelSearchQuery();
    try {
      if (searchQuery) {
        saveHotelSearchQuery(searchQuery);
      }
      const response :any= await api.post(`/search/hotel/details` , {
        ...data,
        searchQuery,
      });
      saveHotelReviewContext({
        hotelId: data?.id,
        correlationId: response.data?.correlationId,
        reviewHash: response.data?.reviewHash,
        searchQuery,
      });
      set({ getDetail:response.data,isLoading: false, error: null });
      return response
    } catch (error:any) {
      const current: any = (get() as any).getDetail;
      const sameHotel =
        current?.hotel && String(current.hotel.id) === String(data?.id);
      if (sameHotel) {
        const nextDetail = {
          ...current,
          searchQuery,
          hotel: {
            ...current.hotel,
            ops: [],
          },
          availability: {
            available: false,
            message: hotelAvailabilityMessage(error),
          },
        };
        set({ getDetail: nextDetail, isLoading: false, error: null });
        return { data: nextDetail };
      }
      set({ error: error, isLoading: false });
    }
  },
//   getFareRule: async (data:any)=>{
//     set({ isLoading: true, error: null }); // Set loading state
//     try {
//       const response :any= await api.post(`/flight/farerule` , data); // Fetch users from an API
//       set({ fareRule:response.data.data.fareRule,isLoading: false, error: null });
//       return response
//     } catch (error:any) {
//       set({ error: error, isLoading: false }); // Handle errors
//     }
//   },
  createSession: async (data:string[])=>{
   
    set({ receiveError:false ,isSessionLoading: true, error: null }); // Set loading state
    try {
      const response:any = await api.post(`/hotel/session` , data); // Fetch users from an API

      set({ session:response.data.session,isSessionLoading: false, error: null,receiveError:false });
      return response
    } catch (error:any) {
      set({ receiveError:true ,error: error, isSessionLoading: false }); // Handle errors
    }
  },

  setIsLoading : (isLoading:boolean) => {
    set({ isLoading: isLoading });
  },
 
  setRequestUrl:()=> (url:string)=>{
    set({requestUrl:url})
  },
  setSessionExpired:()=>(isExpired:boolean)=>{
    set({isSessionExpired:isExpired})
  },
  getCancellationCharges: async (id: number) => {
    set({ isCancellationChargesLoading: true });
    try {
      const response = await api.get(`/hotel/cancellation/charges/${id}`);
      set({ cancellationCharges: response.data, isCancellationChargesLoading: false });
      return response.data;
    } catch (error: any) {
      set({ isCancellationChargesLoading: false, error: error });
    }
  },
  cancelHotelBooking: async (bookingId: string) => {
    set({ isLoading: true, error: null });
    try {
      const response = await api.post(`/hotel/booking/cancel` , {booking_id:bookingId});
      set({ isLoading: false, error: null });
      return response.data;
    } catch (error: any) {
      set({ 
        error: error.response?.data?.message || 'Failed to cancel booking', 
        isLoading: false 
      });
      throw error;
    }
  },
  setIsError:(isError:boolean)=>{
    set({isErrors:isError})
  },

  setSearchNationalities: async (nationalities: any) => {
    set({ isNationalitiesLoading: true });
    try {
      const response = await api.get(`/search/hotel/nationality?value=${nationalities}`); // Fetch users from an API
      if (!response?.data) {
        throw new Error('Network response was not ok');
      }
      const data = response.data;
      set({ isNationalitiesLoading: false, nationalities: data });
      return data;
    } catch (error: any) {
      set({ isNationalitiesLoading: false, error: error });
    }
  },
}));

export default useHotelBookingStore;
